import { Box, Button, Typography } from '@mui/material';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from './Alert.module.scss';
import { memo, useEffect, useState } from 'react';
import { handleApiError } from '~/utils/helper';
import DetailAlertDialog from './component/detail-alert-dialog/DetailAlertDialog';
import ResolveSuccessModal from './component/ResolveSuccessModal';
import { IAlertList } from '~/utils/interface/alert';
import dayjs from 'dayjs';
import BaseRow from '~/components/common/base-row/BaseRow';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { handleGetAlertList } from '~/thunks/alert/alertThunk';
import { alertActions } from '~/thunks/alert/alertSlice';
import AlertList from './components/AlertList';
import { AlertLevelEnum, AlertStatusEnum } from '~/utils/enum';
import BaseDatePicker from '~/components/common/base-date-picker/BaseDatePicker';
import { PAGE_INDEX } from '~/utils/constants/common';

const cx = classNames.bind(styles);

const Alerts = () => {
  const { t } = useTranslation('admin/alert');
  const dispatch = useAppDispatch();

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);
  const username = selectedUser?.username ?? userProfile?.username;

  const { filter, needRefreshData } = useAppSelector((state) => state.alert);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailAlertId, setDetailAlertId] = useState<string | number | null>(null);
  const [resolveSuccessOpen, setResolveSuccessOpen] = useState(false);
  const [selectedAlertSnapshot, setSelectedAlertSnapshot] = useState<{
    imei?: string | null;
    full_name?: string | null;
    person_id?: number | null;
  } | null>(null);

  const levelOptions = [
    { id: AlertLevelEnum.WARNING, name: t('alert_level_options.warning') },
    { id: AlertLevelEnum.CRITICAL, name: t('alert_level_options.critical') },
  ];

  const processingStatusOptions = [
    { id: AlertStatusEnum.UNPROCESSED, name: t('processing_status.1') },
    { id: AlertStatusEnum.PROCESSED, name: t('processing_status.2') },
  ];

  useEffect(() => {
    if (!username) return;

    dispatch(handleGetAlertList({ ...filter, username: username }))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData, username]);

  // Reset when change login
  useEffect(() => {
    return () => {
      dispatch(alertActions.resetFilter());
    };
  }, [dispatch]);

  const handleDetailOpenChange = (open: boolean, needRefreshData?: boolean) => {
    setDetailOpen(open);
    if (!open) {
      setDetailAlertId(null);
      setSelectedAlertSnapshot(null);
    }
    if (needRefreshData) dispatch(alertActions.changeNeedRefreshData(true));
  };

  const handleOpenAlertDetail = (row: IAlertList) => {
    if (row.id == null) return;
    if (row.status !== 1 && row.status !== 2) return;
    setDetailAlertId(row.id);
    setSelectedAlertSnapshot({
      imei: row.imei ?? null,
      full_name: row.full_name ?? null,
      person_id: row.person_id ?? null,
    });
    setDetailOpen(true);
  };

  const handleClearFilter = () => {
    dispatch(alertActions.resetFilter());
  };

  return (
    <Box className={cx('alert-screen')}>
      <Box className='flex items-center bg-[#123B6D] h-20 px-6'>
        <Typography className='text-white font-semibold text-xl bg-[#123B6D]'>{t('list')}</Typography>
      </Box>

      <BaseRow className='gap-4 px-6 pt-6 mb-7 2xl:!items-end max-[1600px]:!flex-col'>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('level.title')}</Typography>
          <BaseSelect
            options={levelOptions}
            value={filter.level ?? ''}
            placeholder={t('select_level')}
            isSearchEnable
            isDisplayAllOption
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                alertActions.setFilter({ level: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
              );
            }}
          />
        </Box>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('processing_status.title')}</Typography>
          <BaseSelect
            options={processingStatusOptions}
            value={filter.status ?? ''}
            placeholder={t('select_processing_status')}
            isSearchEnable
            isDisplayAllOption
            onChange={(e) => {
              const value = e.target.value;
              dispatch(
                alertActions.setFilter({ status: value === '' ? undefined : Number(value), pageIndex: PAGE_INDEX })
              );
            }}
          />
        </Box>
        <Box className='w-full'>
          <Typography className='text-[#161A1F] font-medium text-sm mb-2'>{t('createDate')}</Typography>
          <Box className='flex justify-between items-center gap-3 max-[768px]:!flex-col'>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.startDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    alertActions.setFilter({
                      startDate: dayjs(date).startOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('startDate')}
                calendarProps={filter.endDate ? { maxDate: dayjs(filter.endDate).toDate() } : {}}
                className='w-full'
              />
            </Box>
            <Box className='w-4 h-px bg-black hidden lg:block'></Box>
            <Box className='w-full'>
              <BaseDatePicker
                portalId='datePicker-portalId'
                value={filter.endDate}
                dateFormat='YYYY-MM-DD'
                onChange={(date) =>
                  dispatch(
                    alertActions.setFilter({
                      endDate: dayjs(date).endOf('day').format('YYYY-MM-DD'),
                      pageIndex: PAGE_INDEX,
                    })
                  )
                }
                placeholder={t('endDate')}
                calendarProps={filter.startDate ? { minDate: dayjs(filter.startDate).toDate() } : {}}
                className='w-full'
              />
            </Box>
            <Box className='flex-1'>
              <Button
                className='border border-solid border-[#123B6D] bg-[#DCE8F5] rounded-xl text-base font-bold text-[#123B6D] w-[132px] hover:bg-[#0E2F58] hover:opacity-80 hover:text-white'
                onClick={handleClearFilter}
              >
                {t('clear', { ns: 'admin/common' })}
              </Button>
            </Box>
          </Box>
        </Box>
      </BaseRow>

      <AlertList onOpenDetail={handleOpenAlertDetail} />

      <DetailAlertDialog
        open={detailOpen}
        setOpen={handleDetailOpenChange}
        onResolveSuccess={() => setResolveSuccessOpen(true)}
        data={{
          id: detailAlertId,
          imei: selectedAlertSnapshot?.imei ?? null,
          full_name: selectedAlertSnapshot?.full_name ?? null,
          person_id: selectedAlertSnapshot?.person_id ?? null,
        }}
      />

      <ResolveSuccessModal open={resolveSuccessOpen} onClose={() => setResolveSuccessOpen(false)} />
    </Box>
  );
};

export default memo(Alerts);
